import api from './client';

// Manager types
export interface Manager {
  id: string;
  email: string;
  name: string;
  created_at: string;
}

// League types
export interface League {
  id: string;
  name: string;
  description: string | null;
  owner_id: string;
  player_count: number;
  created_at: string;
  role?: string; // "owner" or "manager"
}

export interface LeaderboardEntry {
  rank: number;
  player_id: string;
  player_username: string;
  total_xp: number;
}

export interface Leaderboard {
  league_id: string;
  league_name: string;
  entries: LeaderboardEntry[];
}

// Invite types
export interface Invite {
  id: string;
  email: string;
  league_id: string;
  league_name: string;
  status: string;
  created_at: string;
  expires_at: string;
  invite_url: string;
}

export interface InviteValidation {
  league_name: string;
  expires_at: string;
  status: string;
}

export interface PlayerLeagueInfo {
  league_id: string | null;
  league_name: string | null;
}

// Player Category Management types
export interface PlayerCategoryItem {
  id: string;
  name: string;
  difficulty: number;
  skill_level: number;
  level?: number;
  is_custom?: boolean;
}

export interface PlayerCategoriesData {
  player_id: string;
  player_username: string;
  assigned: PlayerCategoryItem[];
  available: PlayerCategoryItem[];
}

// Manager Auth API
export const registerManager = async (email: string, password: string, name: string) => {
  const res = await api.post('/api/manager/auth/register', { email, password, name });
  return res.data;
};

export const loginManager = async (email: string, password: string) => {
  const res = await api.post('/api/manager/auth/login', { email, password });
  return res.data;
};

export const getManagerMe = async (): Promise<Manager> => {
  const res = await api.get('/api/manager/auth/me');
  return res.data;
};

// League API (manager only)
export const createLeague = async (name: string, description?: string): Promise<League> => {
  const res = await api.post('/api/leagues', { name, description });
  return res.data;
};

export const listLeagues = async (): Promise<League[]> => {
  const res = await api.get('/api/leagues');
  return res.data.leagues;
};

export const getLeague = async (leagueId: string): Promise<League> => {
  const res = await api.get(`/api/leagues/${leagueId}`);
  return res.data;
};

export const updateLeague = async (leagueId: string, name?: string, description?: string): Promise<League> => {
  const res = await api.put(`/api/leagues/${leagueId}`, { name, description });
  return res.data;
};

export const deleteLeague = async (leagueId: string): Promise<void> => {
  await api.delete(`/api/leagues/${leagueId}`);
};

// Manager leaderboard (requires manager auth)
export const getLeaderboard = async (leagueId: string): Promise<Leaderboard> => {
  const res = await api.get(`/api/leagues/${leagueId}/leaderboard`);
  return res.data;
};

// Player leaderboard (for viewing own league)
export const getMyLeagueLeaderboard = async (): Promise<Leaderboard> => {
  const res = await api.get('/api/me/league/leaderboard');
  return res.data;
};

export const recalculateLeaderboard = async (leagueId: string): Promise<{ updated_count: number }> => {
  const res = await api.post(`/api/leagues/${leagueId}/recalculate-leaderboard`);
  return res.data;
};

// Invite API (manager only)
export const createInvite = async (leagueId: string, email: string): Promise<Invite> => {
  const res = await api.post(`/api/leagues/${leagueId}/invites`, { email });
  return res.data;
};

export const listInvites = async (leagueId: string, status?: string): Promise<Invite[]> => {
  const params = status ? { status } : {};
  const res = await api.get(`/api/leagues/${leagueId}/invites`, { params });
  return res.data.invites;
};

export const cancelInvite = async (leagueId: string, inviteId: string): Promise<void> => {
  await api.delete(`/api/leagues/${leagueId}/invites/${inviteId}`);
};

// Public invite API
export const validateInvite = async (token: string): Promise<InviteValidation> => {
  const res = await api.get(`/api/invites/${token}`);
  return res.data;
};

export const acceptInvite = async (token: string): Promise<{ league_id: string; league_name: string }> => {
  const res = await api.post(`/api/invites/${token}/accept`);
  return res.data;
};

// Player's league info
export const getMyLeague = async (): Promise<PlayerLeagueInfo> => {
  const res = await api.get('/api/me/league');
  return res.data;
};

// Player Category Management API (manager only)
export const getPlayerCategories = async (
  leagueId: string, playerId: string
): Promise<PlayerCategoriesData> => {
  const res = await api.get(`/api/leagues/${leagueId}/players/${playerId}/categories`);
  return res.data;
};

export const updatePlayerCategories = async (
  leagueId: string, playerId: string, categoryIds: string[]
): Promise<PlayerCategoriesData> => {
  const res = await api.put(`/api/leagues/${leagueId}/players/${playerId}/categories`, {
    category_ids: categoryIds,
  });
  return res.data;
};

export const resetPlayerCategories = async (
  leagueId: string, playerId: string
): Promise<PlayerCategoriesData> => {
  const res = await api.post(`/api/leagues/${leagueId}/players/${playerId}/categories/reset`);
  return res.data;
};

// Add Existing Player types
export interface PlayerSearchResult {
  found: boolean;
  player_id?: string;
  username?: string;
  grade?: number;
  has_league?: boolean;
}

export interface AddPlayerResult {
  player_id: string;
  username: string;
  league_id: string;
}

export const searchPlayerForLeague = async (
  leagueId: string, username: string
): Promise<PlayerSearchResult> => {
  const res = await api.get(`/api/leagues/${leagueId}/player-search`, {
    params: { username }
  });
  return res.data;
};

export const addPlayerToLeague = async (
  leagueId: string, playerId: string
): Promise<AddPlayerResult> => {
  const res = await api.post(`/api/leagues/${leagueId}/players`, {
    player_id: playerId
  });
  return res.data;
};

// Custom Flash Card Set types
export interface FlashCardItem {
  question: string;
  correct_answer: string;
  wrong_answers: string[];
}

export interface FlashCardSetSummary {
  id: string;
  name: string;
  description: string | null;
  difficulty: number;
  skill_level: number;
  time_limit_seconds: number;
  card_count: number;
  league_id: string;
  created_at: string;
}

// Custom Flash Card Set API (manager only)
export const generateFlashCards = async (
  leagueId: string,
  prompt: string,
  difficulty: number,
  feedback?: string | null,
): Promise<{ cards: FlashCardItem[] }> => {
  const res = await api.post(`/api/leagues/${leagueId}/flash-card-sets/generate`, {
    prompt,
    difficulty,
    feedback: feedback || undefined,
  });
  return res.data;
};

export const saveFlashCardSet = async (
  leagueId: string,
  data: {
    name: string;
    description?: string;
    difficulty: number;
    skill_level: number;
    time_limit_seconds: number;
    prompt: string;
    cards: FlashCardItem[];
  },
): Promise<FlashCardSetSummary> => {
  const res = await api.post(`/api/leagues/${leagueId}/flash-card-sets`, data);
  return res.data;
};

export const listFlashCardSets = async (leagueId: string): Promise<FlashCardSetSummary[]> => {
  const res = await api.get(`/api/leagues/${leagueId}/flash-card-sets`);
  return res.data;
};

export const deleteFlashCardSet = async (leagueId: string, categoryId: string): Promise<void> => {
  await api.delete(`/api/leagues/${leagueId}/flash-card-sets/${categoryId}`);
};

export interface CategoryCardsResponse {
  category_name: string;
  category_id: string;
  cards: FlashCardItem[];
}

export const getCategoryCards = async (
  leagueId: string, categoryId: string
): Promise<CategoryCardsResponse> => {
  const res = await api.get(`/api/leagues/${leagueId}/categories/${categoryId}/cards`);
  return res.data;
};

// Manager Invitation types
export interface LeagueManager {
  manager_id: string;
  name: string;
  email: string;
  role: string;
  added_at: string;
}

export interface ManagerInvite {
  id: string;
  email: string;
  league_id: string;
  league_name: string;
  inviter_name: string;
  status: string;
  created_at: string;
  expires_at: string;
  invite_url: string;
}

export interface ManagerInviteValidation {
  league_name: string;
  inviter_name: string;
  expires_at: string;
  status: string;
}

// Manager invitation API (manager only)
export const listLeagueManagers = async (leagueId: string): Promise<LeagueManager[]> => {
  const res = await api.get(`/api/leagues/${leagueId}/managers`);
  return res.data.managers;
};

export const inviteManager = async (leagueId: string, email: string): Promise<ManagerInvite> => {
  const res = await api.post(`/api/leagues/${leagueId}/managers/invite`, { email });
  return res.data;
};

export const listManagerInvites = async (leagueId: string): Promise<ManagerInvite[]> => {
  const res = await api.get(`/api/leagues/${leagueId}/managers/invites`);
  return res.data.invites;
};

export const cancelManagerInvite = async (leagueId: string, inviteId: string): Promise<void> => {
  await api.delete(`/api/leagues/${leagueId}/managers/invites/${inviteId}`);
};

export const removeManager = async (leagueId: string, managerId: string): Promise<void> => {
  await api.delete(`/api/leagues/${leagueId}/managers/${managerId}`);
};

// Public manager invite API
export const validateManagerInvite = async (token: string): Promise<ManagerInviteValidation> => {
  const res = await api.get(`/api/manager-invites/${token}`);
  return res.data;
};

export const acceptManagerInvite = async (token: string): Promise<{ league_id: string; league_name: string }> => {
  const res = await api.post(`/api/manager-invites/${token}/accept`);
  return res.data;
};
